import React, { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Pipette, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Input } from '../ui/input';

// Hook to manage recent colors in localStorage
const useRecentColors = (maxColors = 5) => {
  const [recentColors, setRecentColors] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('dekalio_recent_colors');
    if (stored) {
      try {
        setRecentColors(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent colors', e);
      }
    }
  }, []);

  const addRecentColor = (color) => {
    setRecentColors(prev => {
      // Remove if already exists
      const filtered = prev.filter(c => c.toLowerCase() !== color.toLowerCase());
      // Add to beginning
      const updated = [color.toUpperCase(), ...filtered].slice(0, maxColors);
      // Save to localStorage
      localStorage.setItem('dekalio_recent_colors', JSON.stringify(updated));
      return updated;
    });
  };

  return { recentColors, addRecentColor };
};

const ColorPicker = ({ color, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(color);
  const { recentColors, addRecentColor } = useRecentColors();

  const handleHexInputChange = (e) => {
    const value = e.target.value;
    setHexInput(value);
    
    // Validate hex color (with or without #)
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexPattern.test(value)) {
      const normalizedHex = value.startsWith('#') ? value : `#${value}`;
      onChange(normalizedHex);
    }
  };

  const handleColorChange = (newColor) => {
    onChange(newColor);
    setHexInput(newColor);
    addRecentColor(newColor);
  };

  const handleRecentColorClick = (recentColor) => {
    onChange(recentColor);
    setHexInput(recentColor);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="w-9 h-9 rounded-full border-2 border-input bg-background transition-all hover:scale-110 flex items-center justify-center"
            onClick={() => {
              setIsOpen(!isOpen);
              setHexInput(color);
            }}
          >
            <Pipette className="w-4 h-4 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Välj egen färg</p>
        </TooltipContent>
      </Tooltip>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed z-[101] color-picker-modal" style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <div className="bg-background border rounded-lg shadow-xl p-3 space-y-3">
              <HexColorPicker color={color} onChange={handleColorChange} />
              <Input
                type="text"
                value={hexInput}
                onChange={handleHexInputChange}
                placeholder="#000000"
                className="font-mono text-sm"
              />
              
              {recentColors.length > 0 && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-1 mb-2">
                    <Clock size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Senast använda</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {recentColors.map((recentColor) => (
                      <Tooltip key={recentColor}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleRecentColorClick(recentColor)}
                            className="w-8 h-8 rounded-md border-2 border-input transition-all hover:scale-110 hover:border-primary"
                            style={{ backgroundColor: recentColor }}
                            title={recentColor}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-mono text-xs">{recentColor}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ColorPicker;
